/**
 * MainLayout — authenticated portal shell.
 *
 * Composes: Sidebar + mobile overlay + Header + scrollable content.
 *
 * Desktop (lg+):
 *   Sidebar is fixed on the left; content column has a matching left margin.
 *   sidebarCollapsed switches between 260 px and 72 px.
 *
 * Mobile (<lg):
 *   Sidebar slides in as an off-canvas drawer.
 *   A dimmed overlay covers the content behind the open drawer.
 *   Content column has no left margin.
 */

import { Suspense, useEffect, useLayoutEffect } from 'react'
import { Outlet, useLocation }                   from 'react-router-dom'
import { useUiStore }                            from '@store'
import { useIsMobileNav }                      from '@hooks'
import { Sidebar, Header }                       from '@components/layout'
import { Toaster, PageContentLoader }            from '@components/feedback'
import { cn }                                    from '@utils'

export function MainLayout() {
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed)
  const sidebarOpen      = useUiStore((s) => s.sidebarOpen)
  const setSidebarOpen   = useUiStore((s) => s.setSidebarOpen)
  const { pathname }     = useLocation()
  const isMobileNav      = useIsMobileNav()

  const showMobileBackdrop = sidebarOpen && isMobileNav

  // Close drawer synchronously before paint when the route changes.
  useLayoutEffect(() => {
    setSidebarOpen(false)
  }, [pathname, setSidebarOpen])

  // Close drawer when switching to desktop layout.
  useEffect(() => {
    if (!isMobileNav) setSidebarOpen(false)
  }, [isMobileNav, setSidebarOpen])

  // Ensure no stale overlay survives layout unmount.
  useEffect(() => () => setSidebarOpen(false), [setSidebarOpen])

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'var(--ac-bg-base)' }}
    >
      <Sidebar />

      {/* Mobile backdrop — plain div (no exit animation) to avoid stuck overlays */}
      {showMobileBackdrop && (
        <div
          className="fixed inset-0 lg:hidden"
          style={{ background: 'var(--ac-bg-overlay)', zIndex: 250 }}
          aria-hidden="true"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Content column ────────────────────────────────────────── */}
      <div
        className={cn(
          'relative flex-1 flex flex-col min-w-0',
          'transition-[margin-left] duration-250 ease-in-out',
          sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]',
          !showMobileBackdrop && 'z-[1]',
        )}
      >
        <Header />

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {/*
            Suspense here keeps sidebar + header mounted while lazy page chunks load.
            Without this, the outer router Suspense unmounts the entire shell and can
            leave a ghost mobile backdrop on small screens.
          */}
          <Suspense fallback={<PageContentLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <Toaster />
    </div>
  )
}
